import { useState, useRef, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import dayjs from "dayjs";
import "react-day-picker/dist/style.css";

/**
 * Normalize date to YYYY-MM-DD for comparison (timezone-safe).
 */
function toDateKey(d) {
  if (!d) return null;
  return dayjs(d).format("YYYY-MM-DD");
}

/**
 * Check if current value matches a preset range (for active state).
 * For "last 30 days": value.to is today, value.from is 29 days before (inclusive = 30 days).
 */
function rangeMatchesValue(value, presetRange) {
  if (!value?.from || !value?.to || !presetRange?.from || !presetRange?.to) return false;
  const vFrom = toDateKey(value.from);
  const vTo = toDateKey(value.to);
  const pFrom = toDateKey(presetRange.from);
  const pTo = toDateKey(presetRange.to);
  return vFrom === pFrom && vTo === pTo;
}

/**
 * DateRangePicker – trigger + popover with optional presets and calendar range selection.
 * Accepts value { from: Date, to: Date }, onChange(range).
 * Optional presets: [{ id, label, getRange, disabled? }]. When disabled, preset is not clickable.
 * Displays formatted range and closes on full range select or outside click.
 */
const CLOSE_DELAY_MS = 350;

export default function DateRangePicker({ value, onChange, placeholder = "Select range", presets = [] }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const from = value?.from;
  const to = value?.to;

  const displayText =
    from && to
      ? `${dayjs(from).format("D MMM YYYY")} – ${dayjs(to).format("D MMM YYYY")}`
      : from
        ? `${dayjs(from).format("D MMM YYYY")} – …`
        : placeholder;

  const handlePresetClick = (preset) => {
    if (preset.disabled) return;
    const range = preset.getRange();
    if (range?.from && range?.to) {
      onChange?.(range);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    }
  };

  const handleSelect = (range, selectedDay) => {
    if (!selectedDay) return;
    const hadCompleteRange = from && to;
    const nextRange = hadCompleteRange
      ? { from: selectedDay, to: undefined }
      : range?.to
        ? { from: range.from, to: range.to }
        : range?.from
          ? { from: range.from, to: undefined }
          : { from: selectedDay, to: undefined };
    onChange?.(nextRange);
    if (nextRange.from && nextRange.to) {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const hasPresets = Array.isArray(presets) && presets.length > 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span>{displayText}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1 min-w-[280px] rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
          role="dialog"
          aria-label="Select date range"
        >
          {hasPresets && (
            <div className="mb-3">
              <div className="grid grid-cols-2 gap-2">
                {presets.map((preset) => {
                  let isActive = false;
                  if (!preset.disabled) {
                    try {
                      const presetRange = preset.getRange();
                      isActive = rangeMatchesValue(value, presetRange);
                    } catch (_) {
                      // ignore getRange errors
                    }
                  }
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      disabled={preset.disabled}
                      onClick={() => handlePresetClick(preset)}
                      title={preset.disabled ? "Set project start date in project settings" : undefined}
                      className={`min-h-[44px] rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 sm:min-h-0 sm:py-1.5 ${
                        preset.disabled
                          ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                          : isActive
                            ? "border-brand bg-brand/10 text-brand"
                            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className={hasPresets ? "border-t border-gray-200 pt-3" : ""}>
            {hasPresets && (
              <p className="mb-2 text-xs font-medium text-gray-500">Custom range</p>
            )}
            <DayPicker
              mode="range"
              selected={value}
              onSelect={handleSelect}
              defaultMonth={from || to || new Date()}
              numberOfMonths={1}
            />
          </div>
        </div>
      )}
    </div>
  );
}
